import React from 'react';
import { useTranslation } from 'react-i18next';

import type { UserSession } from '@/store/slices/userSessions';

import type { TFunction } from 'i18next';

import { Badge } from '../../../../components';
import { DeviceIcon } from './DeviceIcon';
import { parseUserAgent, type DeviceInfo } from './device';
import { flagEmoji } from './geo';
import { useSessionGeo } from './useSessionGeo';
import { relativeTime } from './time';
import { Row, IconChip, Info, Name, Meta, MetaText, Sep, Flag, Trailing, RowAction } from './SessionRow.styles';

/**
 * Translate a device name at the render site (the names are a fixed, closed
 * vocabulary defined in `device.ts`; that module keeps stable English ids and we
 * localise where they render). Product names (iPhone, iPad, Mac, …) resolve to
 * themselves where no translation exists.
 */
const deviceName = (t: TFunction, device: DeviceInfo): string => {
  switch (device.name) {
    case 'Unknown device':
      return t('Unknown device');
    case 'iPhone':
      return t('iPhone');
    case 'iPad':
      return t('iPad');
    case 'iPod':
      return t('iPod');
    case 'Android phone':
      return t('Android phone');
    case 'Android tablet':
      return t('Android tablet');
    case 'Windows phone':
      return t('Windows phone');
    case 'Windows PC':
      return t('Windows PC');
    case 'Mac':
      return t('Mac');
    case 'Chromebook':
      return t('Chromebook');
    case 'Linux PC':
      return t('Linux PC');
    default:
      return t(device.name);
  }
};

export type SessionRowProps = {
  /** Representative session for this login (richest metadata in the group). */
  session: UserSession;
  /** Every token of this login — revoked together (access + refresh pair). */
  tokens: string[];
  /** This is the login the current browser is authenticated with. */
  isCurrent: boolean;
  /** A revoke request for this login is in flight. */
  revoking: boolean;
  onRevoke: (tokens: string[]) => void;
};

/**
 * One row in the sessions list: device glyph + name, a meta line (country ·
 * last-seen time · browser) and a trailing control — a "This device" badge for
 * the current login, or a "Log out" pill that revokes all of the login's tokens.
 */
export const SessionRow = ({ session, tokens, isCurrent, revoking, onRevoke }: SessionRowProps): React.ReactElement => {
  const { t } = useTranslation();

  // "This device" is authoritatively the current browser, so name it from the
  // live navigator UA — the group's representative login may be an older session
  // (or, in odd data, a different device the refresh chain attached to).
  const ua =
    isCurrent && typeof navigator !== 'undefined' ? navigator.userAgent : session.token_data?.user_agent;
  const device = parseUserAgent(ua);

  const ip = session.token_data?.ip;
  const { geo, loading } = useSessionGeo(ip);
  const flag = geo ? flagEmoji(geo.countryCode) : '';
  // Only claim a location when we have one (or are resolving an IP). With no IP
  // recorded, omit the location entirely rather than repeat "Unknown location"
  // on every metadata-less row.
  const locationText = geo ? geo.country : loading ? t('Locating…') : ip ? t('Unknown location') : null;
  // The timestamp tracks the refresh token, i.e. when the login was last seen —
  // not the original sign-in. Omitted for the current login, where it's "now".
  const lastSeenText = relativeTime(t, session.created_at);

  // Device name localised at the render site (the closed vocabulary lives in device.ts).
  const name = deviceName(t, device);

  const metaParts: { key: string; node: React.ReactNode }[] = [];
  if (locationText) metaParts.push({ key: 'location', node: <MetaText text={locationText} tag="span" /> });
  if (!isCurrent && lastSeenText) {
    metaParts.push({
      key: 'time',
      node: <MetaText text="Last seen {{time}}" placeholders={{ time: lastSeenText }} tag="span" />,
    });
  }
  if (device.browser) metaParts.push({ key: 'browser', node: <MetaText text={device.browser} tag="span" /> });

  const handleRevoke = React.useCallback(() => onRevoke(tokens), [onRevoke, tokens]);

  return (
    <Row>
      <IconChip>
        <DeviceIcon glyph={device.glyph} />
      </IconChip>

      <Info>
        <Name text={name} tag="span" />
        <Meta>
          {flag ? <Flag aria-hidden="true">{flag}</Flag> : null}
          {metaParts.map((part, index) => (
            <React.Fragment key={part.key}>
              {index > 0 ? <Sep aria-hidden="true">·</Sep> : null}
              {part.node}
            </React.Fragment>
          ))}
        </Meta>
      </Info>

      <Trailing>
        {isCurrent ? (
          <Badge label={t('This device')} tone="accent" />
        ) : (
          <RowAction
            type="button"
            onClick={handleRevoke}
            disabled={revoking}
            aria-busy={revoking}
            aria-label={
              revoking ? t('Logging out {{name}}…', { name }) : t('Log out of {{name}}', { name })
            }
          >
            {revoking ? <MetaText text="Logging out…" tag="span" /> : <MetaText text="Log out" tag="span" />}
          </RowAction>
        )}
      </Trailing>
    </Row>
  );
};

export default SessionRow;
